import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Calendar as CalendarIcon,
  Image as ImageIcon,
  X as XIcon,
  ChevronLeft,
  ChevronRight,
  Trophy,
  PartyPopper,
} from "lucide-react";
import MultiPhotoUploader from "@/components/MultiPhotoUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  title: "",
  description: "",
  event_date: "",
  event_type: "",
  status: "Active",
  photos: [],
};

function FilterPill({ label, active, onClick, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-sm px-4 py-2 rounded-sm border transition-colors ${
        active
          ? "bg-[#234333] text-white border-[#234333]"
          : "bg-white text-[#1c1a17] border-[#e5e0d8] hover:bg-[#efece6]"
      }`}
    >
      {label}
      {typeof count === "number" ? (
        <span className={`ml-2 text-[11px] ${active ? "text-white/80" : "text-[#6b655b]"}`}>
          · {count}
        </span>
      ) : null}
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function EventsAchievements() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]); // master-driven engagement types
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(""); // "" | <type-name>
  const [statusFilter, setStatusFilter] = useState(""); // "" | Active | Inactive

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { event, index }

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [evs, tps] = await Promise.all([
        api.get("/institute/events"),
        api.get("/masters/campus_engagement_type"),
      ]);
      setItems(evs.data);
      setTypes(tps.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const visible = useMemo(() => {
    return items.filter((it) => {
      if (typeFilter && it.event_type !== typeFilter) return false;
      if (statusFilter && it.status !== statusFilter) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length, Active: 0, Inactive: 0 };
    items.forEach((it) => {
      if (it.event_type) c[it.event_type] = (c[it.event_type] || 0) + 1;
      if (it.status) c[it.status] = (c[it.status] || 0) + 1;
    });
    return c;
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, event_type: types[0]?.name || "" });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title || "",
      description: it.description || "",
      event_date: it.event_date || "",
      event_type: it.event_type || "Event",
      status: it.status || "Active",
      photos: (it.photos || []).map((p) => ({ url: p.url, public_id: p.public_id })),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.event_date) return toast.error("Event date is required");
    if (!form.event_type) return toast.error("Type is required");
    setSaving(true);
    try {
      const payload = { ...form, title: form.title.trim() };
      if (editing) {
        const { data } = await api.put(`/institute/events/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Event updated");
      } else {
        const { data } = await api.post("/institute/events", payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Event added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const it = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/events/${it.id}`);
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success("Event removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="events-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Stories</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Campus Engagements</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Capture moments that matter — events, ceremonies, recognitions, and wins. Each entry
            can hold up to 10 photos (max 100 KB each) to tell the story. Engagement types are
            managed under the Trust Masters.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            data-testid="events-add-button"
            disabled={types.length === 0}
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm disabled:opacity-60"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Entry
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-6">
        <FilterPill
          label="All"
          count={counts.all}
          active={typeFilter === ""}
          onClick={() => setTypeFilter("")}
          testid="events-filter-all"
        />
        {types.map((t) => (
          <FilterPill
            key={t.id}
            label={t.name}
            count={counts[t.name] || 0}
            active={typeFilter === t.name}
            onClick={() => setTypeFilter(t.name)}
            testid={`events-filter-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
          />
        ))}
        <div className="w-px bg-[#e5e0d8] mx-1" />
        <select
          data-testid="events-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={inputCls + " w-auto"}
        >
          <option value="">All statuses</option>
          <option value="Active">Active only</option>
          <option value="Inactive">Inactive only</option>
        </select>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : visible.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="events-empty"
        >
          <PartyPopper size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">
            {items.length === 0 ? "No entries yet" : "No matches for your filters"}
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {items.length === 0 && types.length === 0
              ? "Ask the Trust Admin to add engagement types under Masters first."
              : items.length === 0 && canEdit
                ? `Click "Add Entry" to create your first entry.`
                : items.length === 0
                  ? "Your Institute Admin hasn't added any yet."
                  : "Try clearing the filters above."}
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="events-grid"
        >
          {visible.map((it) => {
            const cover = (it.photos || [])[0];
            const isAchievement = (it.event_type || "").toLowerCase().includes("achievement");
            const TypeIcon = isAchievement ? Trophy : PartyPopper;
            return (
              <article
                key={it.id}
                data-testid={`events-card-${it.id}`}
                className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col"
              >
                <button
                  type="button"
                  onClick={() => (cover ? setLightbox({ event: it, index: 0 }) : null)}
                  disabled={!cover}
                  className="block w-full aspect-[16/10] bg-[#f9f8f6] overflow-hidden relative disabled:cursor-default"
                >
                  {cover ? (
                    <img
                      src={cover.url}
                      alt={it.title}
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#234333]">
                      <ImageIcon size={32} strokeWidth={1.5} />
                    </div>
                  )}
                  {(it.photos || []).length > 1 && (
                    <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] px-2 py-0.5 rounded-sm">
                      +{it.photos.length - 1} more
                    </span>
                  )}
                  <span
                    className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${
                      isAchievement
                        ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#7a6300]"
                        : "border-[#234333]/40 bg-white/90 text-[#234333]"
                    }`}
                  >
                    <TypeIcon size={11} strokeWidth={1.5} />
                    {it.event_type}
                  </span>
                  {it.status === "Inactive" && (
                    <span className="absolute top-2 right-2 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border border-[#9e2a2b]/40 bg-white/90 text-[#9e2a2b]">
                      Inactive
                    </span>
                  )}
                </button>

                <div className="p-4 flex-1 flex flex-col">
                  <div className="font-serif text-xl text-[#1c1a17] leading-tight line-clamp-2">
                    {it.title}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#6b655b] tracking-wide uppercase">
                    <CalendarIcon size={11} strokeWidth={1.5} />
                    {formatDate(it.event_date)}
                  </div>
                  {it.description && (
                    <p
                      className="mt-2 text-sm text-[#6b655b] line-clamp-3 whitespace-pre-line"
                      data-testid={`events-desc-${it.id}`}
                    >
                      {it.description}
                    </p>
                  )}
                  {canEdit && (
                    <div className="mt-auto pt-3 flex gap-1 border-t border-[#e5e0d8] mt-3">
                      <button
                        onClick={() => openEdit(it)}
                        data-testid={`events-edit-${it.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <Pencil size={12} strokeWidth={1.5} /> Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(it)}
                        data-testid={`events-delete-${it.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={12} strokeWidth={1.5} /> Delete
                      </button>
                      {(it.photos || []).length > 0 && (
                        <button
                          onClick={() => setLightbox({ event: it, index: 0 })}
                          data-testid={`events-view-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm ml-auto"
                        >
                          <ImageIcon size={12} strokeWidth={1.5} /> Photos ({it.photos.length})
                        </button>
                      )}
                    </div>
                  )}
                  {!canEdit && (it.photos || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#e5e0d8]">
                      <button
                        onClick={() => setLightbox({ event: it, index: 0 })}
                        data-testid={`events-view-${it.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <ImageIcon size={12} strokeWidth={1.5} /> View {it.photos.length} photo
                        {it.photos.length === 1 ? "" : "s"}
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          event={lightbox.event}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(idx) => setLightbox((l) => ({ ...l, index: idx }))}
        />
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="events-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Entry" : "Add Campus Engagement"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6b655b]">
              Add a title, the date it happened, a short description, and up to 10 photos.
              Inactive entries stay in the system but are hidden from filtered views.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <label className="block md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Title *
              </div>
              <input
                data-testid="events-title-input"
                className={inputCls}
                maxLength={200}
                placeholder="e.g., Foundation Day 2025"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </label>

            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Type *
              </div>
              <select
                data-testid="events-type-select"
                className={inputCls}
                value={form.event_type}
                onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
              >
                <option value="">— Select —</option>
                {types.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Date *
              </div>
              <input
                data-testid="events-date-input"
                type="date"
                className={inputCls}
                value={form.event_date}
                onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Status *
              </div>
              <select
                data-testid="events-status-select"
                className={inputCls}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>

            <label className="block md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Description
              </div>
              <textarea
                data-testid="events-description-input"
                className={inputCls + " min-h-[120px] resize-y"}
                placeholder="Describe the event or achievement — context, participants, outcome."
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>

            <div className="md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Photos
              </div>
              <MultiPhotoUploader
                kind="inst_event_photo"
                photos={form.photos}
                onChange={(photos) => setForm((f) => ({ ...f, photos }))}
                max={10}
                maxBytes={100 * 1024}
                humanMax="100 KB"
                testid="events-photos"
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="events-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="events-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this entry?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              "{confirmDelete?.title}" and all its photos will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="events-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Lightbox({ event, index, onClose, onIndex }) {
  const photos = event.photos || [];
  const cur = photos[index];

  const go = (delta) => {
    const next = (index + delta + photos.length) % photos.length;
    onIndex(next);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, photos.length]);

  if (!cur) {
    onClose();
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="events-lightbox"
    >
      <button
        type="button"
        className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
        onClick={onClose}
        data-testid="events-lightbox-close"
      >
        <XIcon size={22} />
      </button>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            data-testid="events-lightbox-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 rounded-sm bg-black/30"
            aria-label="Previous"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            data-testid="events-lightbox-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 rounded-sm bg-black/30"
            aria-label="Next"
          >
            <ChevronRight size={26} />
          </button>
        </>
      )}

      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={cur.url}
          alt={event.title}
          className="max-w-full max-h-[78vh] object-contain"
        />
        <figcaption className="mt-4 text-white text-center">
          <div className="font-serif text-2xl">{event.title}</div>
          <div className="mt-1 text-sm text-white/70 tracking-wide uppercase">
            {event.event_type} · {formatDate(event.event_date)}
            {photos.length > 1 ? ` · ${index + 1} / ${photos.length}` : ""}
          </div>
        </figcaption>
      </figure>
    </div>
  );
}
